import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import ErrorFallback from "./components/ErrorFallback";
import {
  installChunkReloadHandler,
  clearStaleChunkGuard,
} from "./lib/chunkReload";
import { scrubBreadcrumb, scrubEvent } from "./lib/sentryScrub";
import { installViewAsFetch } from "./lib/viewAs";
import "./index.css";

// Stale-chunk auto-recovery (#1529): wire BEFORE React renders so a dead
// hashed chunk from a previous build silently reloads the tab once instead of
// crashing to the error boundary.
installChunkReloadHandler();

// Read-only "View as agent" (D2-SEC-011, #3944): while a session is held, every
// same-origin /api/ call carries its token. Installed before any request.
installViewAsFetch();

// Initialize Sentry when a DSN is configured; no-op when empty.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "production",
    tracesSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_TRACES_RATE || "0.1",
    ),
    // POPIA (#3303): the SDK records a breadcrumb URL for every fetch/xhr/
    // navigation, query string included -- e.g. /api/v1/search/?q=<client
    // name>. Strip it before anything crosses the border. No-op when the DSN
    // is empty, because init never runs.
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: scrubEvent,
  });
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error} resetError={resetError} />
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

// The app rendered successfully, so any prior one-shot reload guard has done
// its job -- clear it so a FUTURE deploy (a fresh stale-chunk event in this
// long-lived tab) is allowed to recover again. Deferred to the next frame so
// it runs after the initial mount paints.
requestAnimationFrame(() => {
  clearStaleChunkGuard();
});
